// src/components/eventos/views/EventoPublicoConteudo.js
// Componente visual puro — recebe `evento` como prop.
// Usado tanto por EventoPublico (/evento/:id) quanto por EventoAoVivo (/ao-vivo/:clienteId).

import React, { useRef, useEffect } from 'react';

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
export const formatarDataCompleta = (dataString) => {
    if (!dataString) return { diaSemana: '', data: 'Data não informada' };
    try {
        const [ano, mes, dia] = dataString.split('T')[0].split('-').map(Number);
        const meses = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];
        const dias = ['Domingo','Segunda-feira','Terça-feira','Quarta-feira','Quinta-feira','Sexta-feira','Sábado'];
        const dt = new Date(ano, mes - 1, dia);
        return { diaSemana: dias[dt.getDay()], data: `${dia} de ${meses[mes - 1]} de ${ano}` };
    } catch { return { diaSemana: '', data: 'Data inválida' }; }
};

export const calcularDuracao = (inicio, fim) => {
    if (!inicio || !fim) return null;
    const [hI, mI] = inicio.split(':').map(Number);
    const [hF, mF] = fim.split(':').map(Number);
    let min = (hF * 60 + mF) - (hI * 60 + mI);
    if (min < 0) min += 1440;
    if (min === 0) return null;
    const h = Math.floor(min / 60), m = min % 60;
    return h > 0 ? (m > 0 ? `${h}h ${m}min` : `${h}h`) : `${m}min`;
};

const calcularDuracaoMin = (inicio, fim) => {
    if (!inicio || !fim) return 0;
    const [hI, mI] = inicio.split(':').map(Number);
    const [hF, mF] = fim.split(':').map(Number);
    let min = (hF * 60 + mF) - (hI * 60 + mI);
    if (min < 0) min += 1440;
    return min;
};

/* ─── Ícones ──────────────────────────────────────────────────────────────── */
const CalendarIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
);
const ClockIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
);
const MapPinIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
    </svg>
);
const UserIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
);
const CheckIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);
const PlayIcon = () => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
        <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
);
const ListChecksIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/>
        <polyline points="3 6 4 7 6 5"/><polyline points="3 12 4 13 6 11"/><polyline points="3 18 4 19 6 17"/>
    </svg>
);
const ZapIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
);

/* ─── Componente visual ───────────────────────────────────────────────────── */
const EventoPublicoConteudo = ({ evento, cliente }) => {
    const logoSrc = cliente?.logo_url || '/logo-horizontal.png';
    const tarefas  = evento?.tarefas || [];
    const activeTaskRef = useRef(null);

    // Scroll para tarefa ativa a cada refresh
    useEffect(() => {
        if (activeTaskRef.current) {
            activeTaskRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [evento]);
    const total    = tarefas.length;
    const concluidas  = tarefas.filter(t => t.status === 'concluida').length;
    const emAndamento = tarefas.filter(t => t.status === 'em_andamento').length;
    const pendentes   = tarefas.filter(t => t.status === 'pendente' || !t.status).length;
    const pct         = total > 0 ? Math.round((concluidas / total) * 100) : 0;
    const duracaoTotal = calcularDuracaoMin(evento?.hora_inicio, evento?.hora_fim);
    const dateInfo     = evento ? formatarDataCompleta(evento.data) : null;
    const status       = evento?.status;

    const statusConfig = {
        em_andamento: { label: 'Em andamento', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', dot: true },
        concluido:    { label: 'Concluído',    color: '#16a34a', bg: '#f0fdf4', border: '#86efac', dot: false },
        agendado:     { label: 'Agendado',     color: '#d97706', bg: '#fffbeb', border: '#fcd34d', dot: false },
    };
    const sc = statusConfig[status] || statusConfig.agendado;

    return (
        <>
            <style>{`
                @keyframes ep-spin   { to { transform: rotate(360deg); } }
                @keyframes ep-pulse  { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:.5;transform:scale(.85);} }
                @keyframes ep-fadein { from{opacity:0;transform:translateY(12px);} to{opacity:1;transform:translateY(0);} }
                @keyframes ep-live   { 0%,100%{box-shadow:0 0 0 0 rgba(37,99,235,0.35);} 70%{box-shadow:0 0 0 6px rgba(37,99,235,0);} }

                .ep-card { animation: ep-fadein 0.4s ease both; }
                .ep-tarefa-wrap { position:relative; padding-left:48px; }
                .ep-tarefa-wrap::before { content:''; position:absolute; left:15px; top:40px; bottom:-12px; width:2px; background:linear-gradient(to bottom, #e2e8f0, transparent); }
                .ep-tarefa-wrap:last-child::before { display:none; }

                .ep-tarefa-card { background:#ffffff; border:1px solid #e8edf2; border-radius:14px; padding:18px 20px; transition: box-shadow 0.2s, border-color 0.2s; box-shadow:0 1px 3px rgba(0,0,0,0.05); }
                .ep-tarefa-card:hover { box-shadow:0 4px 16px rgba(0,0,0,0.08); border-color:#cbd5e1; }
                .ep-tarefa-card.ativa { border-color:#2563eb; border-left:4px solid #2563eb; background: linear-gradient(135deg, #eff6ff 0%, #f8fbff 100%); }
                @keyframes ep-card-pulse {
                    0%,100% { box-shadow: 0 4px 24px rgba(37,99,235,0.14), 0 1px 4px rgba(37,99,235,0.08); }
                    50%     { box-shadow: 0 6px 32px rgba(37,99,235,0.26), 0 2px 8px rgba(37,99,235,0.14); }
                }
                .ep-tarefa-card.ativa { animation: ep-card-pulse 2.5s ease-in-out infinite; }
                .ep-tarefa-card.concluida { background:#fafafa; border-color:#f1f5f9; opacity:0.75; }

                .ep-dot { position:absolute; left:8px; top:20px; width:16px; height:16px; border-radius:50%; display:flex; align-items:center; justify-content:center; z-index:2; flex-shrink:0; }
                .ep-dot.pendente  { background:#f1f5f9; border:2px solid #e2e8f0; }
                .ep-dot.ativa     { background:#2563eb; border:2px solid #2563eb; animation:ep-live 1.5s ease-in-out infinite; }
                .ep-dot.concluida { background:#2CCB90; border:2px solid #2CCB90; }

                .ep-stat { flex:1; min-width:80px; background:#ffffff; border:1px solid #e8edf2; border-radius:12px; padding:14px 16px; }
                .ep-progress-bar { height:6px; background:#e2e8f0; border-radius:100px; overflow:hidden; }
                .ep-progress-fill { height:100%; border-radius:100px; background:linear-gradient(90deg,#2CCB90,#2DB8E1); transition:width 0.6s ease; }

                @media(max-width:600px){ .ep-hero-meta { flex-direction:column !important; gap:8px !important; } .ep-stats-row { flex-wrap:wrap !important; } }
                @media print { header, .ep-no-print { display:none !important; } .ep-tarefa-card { break-inside:avoid; box-shadow:none !important; border:1px solid #e2e8f0 !important; } }
            `}</style>

            <div style={{ minHeight:'100vh', background:'#f0faf6', fontFamily:"'Inter',-apple-system,BlinkMacSystemFont,sans-serif" }}>

                {/* Navbar */}
                <header style={{ position:'sticky', top:0, zIndex:50, background:'rgba(255,255,255,0.92)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)', borderBottom:'1px solid #e2e8f0' }}>
                    <div style={{ maxWidth:'860px', margin:'0 auto', padding:'0 20px', height:'60px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <img src={logoSrc} alt="Logo" style={{ height:'52px', objectFit:'contain', maxWidth:'200px' }} onError={e => { e.target.src='/logo-horizontal.png'; }} />
                        <span style={{ fontSize:'11px', fontWeight:600, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em' }}>Programação</span>
                    </div>
                </header>

                <main style={{ maxWidth:'860px', margin:'0 auto', padding:'32px 20px 80px' }}>

                    {/* HERO */}
                    <div className="ep-card" style={{ background:'#ffffff', border:'1px solid #e8edf2', borderRadius:'20px', padding:'28px 28px 24px', marginBottom:'16px', boxShadow:'0 2px 8px rgba(0,0,0,0.05)' }}>
                        <div style={{ marginBottom:'16px' }}>
                            <span style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'4px 12px', borderRadius:'100px', background:sc.bg, border:`1px solid ${sc.border}`, color:sc.color, fontSize:'11px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                                {sc.dot && <span style={{ width:'7px', height:'7px', borderRadius:'50%', background:sc.color, animation:'ep-pulse 1.4s ease-in-out infinite', flexShrink:0 }} />}
                                {sc.label}
                            </span>
                        </div>
                        <h1 style={{ margin:'0 0 20px', fontSize:'clamp(24px,5vw,36px)', fontWeight:800, color:'#0f172a', letterSpacing:'-0.03em', lineHeight:1.15 }}>
                            {evento.titulo}
                        </h1>
                        <div className="ep-hero-meta" style={{ display:'flex', flexWrap:'wrap', gap:'10px', marginBottom: evento.descricao ? '20px' : 0 }}>
                            <div style={{ display:'inline-flex', alignItems:'center', gap:'7px', padding:'7px 14px', borderRadius:'10px', background:'rgba(139,92,246,0.07)', border:'1px solid rgba(139,92,246,0.15)', color:'#7c3aed', fontSize:'13px', fontWeight:600 }}>
                                <CalendarIcon />
                                <span>{dateInfo?.diaSemana && <span style={{ opacity:0.7, marginRight:'4px' }}>{dateInfo.diaSemana},</span>}{dateInfo?.data}</span>
                            </div>
                            {(evento.hora_inicio || evento.hora_fim) && (
                                <div style={{ display:'inline-flex', alignItems:'center', gap:'7px', padding:'7px 14px', borderRadius:'10px', background:'rgba(37,99,235,0.06)', border:'1px solid rgba(37,99,235,0.14)', color:'#2563eb', fontSize:'13px', fontWeight:600 }}>
                                    <ClockIcon size={15} />
                                    {evento.hora_inicio}{evento.hora_fim && ` – ${evento.hora_fim}`}
                                    {duracaoTotal > 0 && <span style={{ opacity:0.6, fontSize:'12px', fontWeight:500 }}>· {calcularDuracao(evento.hora_inicio, evento.hora_fim)}</span>}
                                </div>
                            )}
                            {evento.local && (
                                <div style={{ display:'inline-flex', alignItems:'center', gap:'7px', padding:'7px 14px', borderRadius:'10px', background:'rgba(44,203,144,0.07)', border:'1px solid rgba(44,203,144,0.18)', color:'#16a972', fontSize:'13px', fontWeight:600 }}>
                                    <MapPinIcon />{evento.local}
                                </div>
                            )}
                        </div>
                        {evento.descricao && (
                            <p style={{ margin:0, fontSize:'14px', color:'#64748b', lineHeight:1.7, borderTop:'1px solid #f1f5f9', paddingTop:'16px' }}>
                                {evento.descricao}
                            </p>
                        )}
                    </div>

                    {/* STATS */}
                    {total > 0 && (
                        <div className="ep-card" style={{ background:'#ffffff', border:'1px solid #e8edf2', borderRadius:'16px', padding:'20px', marginBottom:'16px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)', animationDelay:'60ms' }}>
                            <div style={{ marginBottom:'16px' }}>
                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                                    <span style={{ fontSize:'12px', fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em' }}>Progresso</span>
                                    <span style={{ fontSize:'13px', fontWeight:700, color: pct === 100 ? '#16a34a' : '#0f172a' }}>{pct}%</span>
                                </div>
                                <div className="ep-progress-bar"><div className="ep-progress-fill" style={{ width:`${pct}%` }} /></div>
                            </div>
                            <div className="ep-stats-row" style={{ display:'flex', gap:'10px' }}>
                                <div className="ep-stat" style={{ textAlign:'center' }}>
                                    <p style={{ margin:'0 0 2px', fontSize:'22px', fontWeight:800, color:'#0f172a', letterSpacing:'-0.02em' }}>{total}</p>
                                    <p style={{ margin:0, fontSize:'11px', color:'#94a3b8', fontWeight:500 }}>Total</p>
                                </div>
                                <div className="ep-stat" style={{ textAlign:'center' }}>
                                    <p style={{ margin:'0 0 2px', fontSize:'22px', fontWeight:800, color:'#16a34a', letterSpacing:'-0.02em' }}>{concluidas}</p>
                                    <p style={{ margin:0, fontSize:'11px', color:'#94a3b8', fontWeight:500 }}>Concluídos</p>
                                </div>
                                {emAndamento > 0 && (
                                    <div className="ep-stat" style={{ textAlign:'center' }}>
                                        <p style={{ margin:'0 0 2px', fontSize:'22px', fontWeight:800, color:'#2563eb', letterSpacing:'-0.02em' }}>{emAndamento}</p>
                                        <p style={{ margin:0, fontSize:'11px', color:'#94a3b8', fontWeight:500 }}>Ao vivo</p>
                                    </div>
                                )}
                                <div className="ep-stat" style={{ textAlign:'center' }}>
                                    <p style={{ margin:'0 0 2px', fontSize:'22px', fontWeight:800, color:'#94a3b8', letterSpacing:'-0.02em' }}>{pendentes}</p>
                                    <p style={{ margin:0, fontSize:'11px', color:'#94a3b8', fontWeight:500 }}>Pendentes</p>
                                </div>
                                {evento.modo_automatico && (
                                    <div className="ep-stat" style={{ textAlign:'center', borderColor:'rgba(44,203,144,0.25)', background:'rgba(44,203,144,0.04)' }}>
                                        <p style={{ margin:'0 0 2px', fontSize:'13px', fontWeight:700, color:'#2CCB90', display:'flex', alignItems:'center', justifyContent:'center', gap:'4px' }}>
                                            <ZapIcon /> Auto
                                        </p>
                                        <p style={{ margin:0, fontSize:'11px', color:'#94a3b8', fontWeight:500 }}>Modo automático</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* PROGRAMAÇÃO */}
                    <div className="ep-card" style={{ animationDelay:'100ms' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'20px' }}>
                            <div style={{ width:'32px', height:'32px', borderRadius:'9px', background:'rgba(44,203,144,0.1)', border:'1px solid rgba(44,203,144,0.2)', display:'flex', alignItems:'center', justifyContent:'center', color:'#2CCB90' }}>
                                <ListChecksIcon />
                            </div>
                            <div>
                                <h2 style={{ margin:0, fontSize:'15px', fontWeight:700, color:'#0f172a' }}>Programação</h2>
                                {total > 0 && <p style={{ margin:0, fontSize:'12px', color:'#94a3b8' }}>{total} {total === 1 ? 'item' : 'itens'}</p>}
                            </div>
                        </div>

                        {total === 0 ? (
                            <div style={{ textAlign:'center', padding:'48px 24px', background:'#ffffff', borderRadius:'14px', border:'1px solid #e2e8f0' }}>
                                <p style={{ margin:0, fontSize:'15px', fontWeight:600, color:'#64748b' }}>Nenhum item na programação</p>
                            </div>
                        ) : (
                            <div style={{ display:'flex', flexDirection:'column', gap:'0' }}>
                                {tarefas.map((tarefa, idx) => {
                                    const ativa     = tarefa.status === 'em_andamento';
                                    const concluida = tarefa.status === 'concluida';
                                    const duracao   = calcularDuracao(tarefa.horaInicio, tarefa.horaFim);
                                    const temHorarioReal = tarefa.horaInicioReal && tarefa.horaInicioReal !== tarefa.horaInicio;
                                    const items     = Array.isArray(tarefa.descricao_items) ? tarefa.descricao_items.filter(i => i) : [];
                                    const isLista   = (tarefa.descricao_tipo === 'lista' || items.length > 0) && items.length > 0;
                                    const dotClass  = ativa ? 'ativa' : concluida ? 'concluida' : 'pendente';

                                    return (
                                        <div key={tarefa.id || idx} ref={ativa ? activeTaskRef : null} className="ep-tarefa-wrap" style={{ marginBottom: idx < tarefas.length - 1 ? '12px' : 0 }}>
                                            <div className={`ep-dot ${dotClass}`}>
                                                {concluida && <CheckIcon />}
                                                {ativa     && <PlayIcon  />}
                                            </div>
                                            <div className={`ep-tarefa-card ${ativa ? 'ativa' : ''} ${concluida ? 'concluida' : ''}`}>
                                                <div style={{ display:'flex', alignItems:'flex-start', gap:'12px' }}>
                                                    <div style={{ flex:1, minWidth:0 }}>
                                                        <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', marginBottom:'6px' }}>
                                                            <span style={{ fontSize:'15px', fontWeight:700, color: concluida ? '#94a3b8' : '#0f172a', textDecoration: concluida ? 'line-through' : 'none', textDecorationColor:'#cbd5e1', lineHeight:1.3 }}>
                                                                {tarefa.titulo || `Item ${idx + 1}`}
                                                            </span>
                                                            {ativa && (
                                                                <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', padding:'4px 10px', borderRadius:'100px', background:'#2563eb', color:'#fff', fontSize:'11px', fontWeight:700, letterSpacing:'0.06em', boxShadow:'0 2px 8px rgba(37,99,235,0.35)' }}>
                                                                    <span style={{ width:'6px', height:'6px', borderRadius:'50%', background:'#fff', animation:'ep-pulse 1s ease-in-out infinite', flexShrink:0 }} />
                                                                    AO VIVO
                                                                </span>
                                                            )}
                                                            {concluida && (
                                                                <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', padding:'2px 8px', borderRadius:'100px', background:'#f0fdf4', border:'1px solid #86efac', color:'#16a34a', fontSize:'10px', fontWeight:700 }}>
                                                                    <CheckIcon /> CONCLUÍDO
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{ display:'flex', flexWrap:'wrap', gap:'10px', alignItems:'center' }}>
                                                            {tarefa.horaInicio && (
                                                                <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', fontSize:'12px', fontWeight:700, color: ativa ? '#2563eb' : concluida ? '#94a3b8' : '#475569', background: ativa ? 'rgba(37,99,235,0.08)' : 'transparent', padding: ativa ? '2px 8px' : '0', borderRadius: ativa ? '6px' : '0' }}>
                                                                    <ClockIcon />
                                                                    {temHorarioReal
                                                                        ? <>{tarefa.horaInicioReal}{tarefa.horaFimReal ? `–${tarefa.horaFimReal}` : ''} <span style={{ opacity:0.5, fontWeight:400, textDecoration:'line-through', fontSize:'11px' }}>{tarefa.horaInicio}{tarefa.horaFim ? `–${tarefa.horaFim}` : ''}</span></>
                                                                        : <>{tarefa.horaInicio}{tarefa.horaFim ? `–${tarefa.horaFim}` : ''}</>
                                                                    }
                                                                </span>
                                                            )}
                                                            {duracao && <span style={{ fontSize:'11px', fontWeight:600, color:'#94a3b8', background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:'6px', padding:'1px 7px' }}>{duracao}</span>}
                                                            {tarefa.local && <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', fontSize:'12px', color:'#64748b', fontWeight:500 }}><MapPinIcon /> {tarefa.local}</span>}
                                                        </div>
                                                        {tarefa.responsavel && (
                                                            <div style={{ display:'inline-flex', alignItems:'center', gap:'5px', marginTop:'6px', fontSize:'12px' }}>
                                                                <UserIcon />
                                                                <span style={{ color:'#94a3b8' }}>Responsável:</span>
                                                                <span style={{ color:'#475569', fontWeight:600 }}>{tarefa.responsavel}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                {isLista ? (
                                                    <ul style={{ margin:'12px 0 0', padding:'12px 16px', background:'#f8fafc', borderRadius:'10px', border:'1px solid #f1f5f9', listStyle:'none' }}>
                                                        {items.map((item, i) => (
                                                            <li key={i} style={{ display:'flex', alignItems:'flex-start', gap:'8px', padding:'4px 0', fontSize:'13px', color:'#475569', lineHeight:1.5, borderBottom: i < items.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                                                <span style={{ width:'18px', height:'18px', borderRadius:'5px', background:'rgba(44,203,144,0.1)', border:'1px solid rgba(44,203,144,0.2)', display:'flex', alignItems:'center', justifyContent:'center', color:'#2CCB90', flexShrink:0, marginTop:'1px' }}><CheckIcon /></span>
                                                                {String(item)}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : tarefa.descricao ? (
                                                    <p style={{ margin:'10px 0 0', fontSize:'13px', color:'#64748b', lineHeight:1.6, padding:'10px 14px', background:'#f8fafc', borderRadius:'10px', border:'1px solid #f1f5f9' }}>
                                                        {tarefa.descricao}
                                                    </p>
                                                ) : null}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* FOOTER */}
                    <div style={{ marginTop:'48px', textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', gap:'8px' }}>
                        <div style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'5px 14px', borderRadius:'8px', background:'#ffffff', border:'1px solid #e2e8f0' }}>
                            <img src={logoSrc} alt="Logo" style={{ height:'18px', objectFit:'contain' }} onError={e => { e.target.src='/logo-horizontal.png'; }} />
                        </div>
                        <p style={{ margin:0, fontSize:'12px', color:'#94a3b8', fontWeight:500 }}>
                            Programação gerada pelo <span style={{ color:'#2CCB90', fontWeight:600 }}>Evlo</span>
                        </p>
                    </div>
                </main>
            </div>
        </>
    );
};

export default EventoPublicoConteudo;
